import ISagaState from "models/ISagaState";
import { action, makeObservable, observable, runInAction } from "mobx";
import { ServiceError } from "services/BaseService";
import { AuthStore } from "stores/AuthStore";
import { BaseStore } from "stores/BaseStore";
import * as RootStore from "stores/RootStore";
import { PipelineService } from "../../../../../services/PipelineService";

export interface SagaLoadError {
    title: string;
    message: string;
}

@RootStore.AcxStore
export default class SagaInspectorStore extends BaseStore {
    readonly authStore: AuthStore;
    readonly pipelineService: PipelineService;

    @observable public sagas?: ISagaState[];
    @observable public isLoading = false;
    @observable public loadError?: SagaLoadError;
    @observable public lastCheckedAt?: Date;

    private requestController?: AbortController;
    private requestNumber = 0;

    constructor(public rootStore: RootStore.IRootStore) {
        super("SagaInspectorStore");
        makeObservable(this);
        this.pipelineService = new PipelineService();
        this.authStore = rootStore.getStore(AuthStore);
    }

    @action
    public reset() {
        this.requestController?.abort();
        this.requestNumber += 1;
        this.sagas = undefined;
        this.loadError = undefined;
        this.isLoading = false;
        this.lastCheckedAt = undefined;
    }

    @action
    public async getAllSagasState(amdId: string, clearExisting = true) {
        const orgId = this.authStore.activeOrgId;
        if (!orgId) return;

        this.requestController?.abort();
        const controller = new AbortController();
        const requestNumber = ++this.requestNumber;
        this.requestController = controller;

        if (clearExisting) this.sagas = undefined;
        this.loadError = undefined;
        this.isLoading = true;

        try {
            const sagas = await this.pipelineService.getAllSagasStateByAMD(
                orgId,
                amdId,
                controller.signal,
            );

            if (requestNumber !== this.requestNumber) return;

            runInAction(() => {
                this.sagas = sagas;
                this.lastCheckedAt = new Date();
            });
        } catch (error) {
            if (
                controller.signal.aborted ||
                requestNumber !== this.requestNumber
            ) {
                return;
            }

            runInAction(() => {
                this.loadError =
                    error instanceof ServiceError &&
                    error.response.status === 404
                        ? {
                              title: "AMD not found",
                              message:
                                  "The requested AMD could not be found in this organization.",
                          }
                        : {
                              title: "Unable to load saga state",
                              message:
                                  "We couldn't load saga state for this AMD.",
                          };
            });
        } finally {
            if (requestNumber === this.requestNumber) {
                runInAction(() => {
                    this.isLoading = false;
                });
            }
        }
    }
}
